// 2.4 — quick-reply chips above the composer.
//
// Shown while the next required field is a radio, derived LIVE from current
// form state on every render. Tapping a chip sends its label as an ordinary
// message through the normal fill flow — there is no side-channel that
// writes the value directly, so the receipt is produced the same way as for
// typed text. The chips disappear on their own once the field stops being
// next-required.
//
// Adversarial review F11 / hygiene closure, store tracker 6.55: an earlier
// version of this component suppressed the row whenever the conversation's
// literal last message was ANY client-composed acknowledgement (discard,
// submit handoff, an update confirmation, or the activation turn's
// graceful-degrade fallback). That over-corrected — an UNRELATED local note
// (e.g. a different, already-submitted form's post-submit-correction "I've
// updated your…" landing in the shared conversation log while THIS form is
// still actively mid-fill) is not the same thing as "no question was
// actually asked", and must not suppress chips for a field that is still
// genuinely next-required. The ONE reachable case task 1.2 actually guards
// against is the activation turn's own degrade fallback (`unaskedAck` on the
// message, set only there — see `FormsContext.tsx`'s `runActivationTurn`):
// a client-composed Start/Switch acknowledgement standing in for a turn that
// produced no usable answer, which never asked about any field. Discard,
// submit handoff, and an update confirmation do NOT set `unaskedAck` and do
// not suppress this row.
//
// Chips are single-use per field (store tracker 6.21): the first tap hides the
// row for that field immediately, so turn latency cannot collect a second
// answer to the same question. The marker clears when the tapped turn settles —
// on success the field has committed and is no longer next-required, so the row
// stays gone on its own; on failure the field is still open and the cleared
// marker brings the chips back so the student can answer again.

import { useContext, useState } from 'react';
import { AppContext } from '../App/AppContext';
import styles from './ConversationalForms.module.css';
import { useConversationalForms } from './FormsContext';

const QuickReplyChips = () => {
    const { active, state, nextRequiredField, sendTurn } = useConversationalForms();
    const { state: appState } = useContext(AppContext);
    // The field id whose chip row is spent while its turn is in flight. Local
    // component state on purpose: the marker is a tap-latency guard, not form
    // state — it must not persist, and a remount (reload) rightly forgets it.
    const [pendingFieldId, setPendingFieldId] = useState<string | null>(null);

    if (!active || !state.activeFormId) return null;
    if (!nextRequiredField || nextRequiredField.type !== 'radio') return null;
    if (pendingFieldId === nextRequiredField.id) return null;
    const options = nextRequiredField.options;
    if (!options?.length) return null;

    // Hidden only when the literal last message is the activation turn's own
    // unasked acknowledgement — never for any other local note.
    const messages = appState.messages;
    const last = messages.length ? messages[messages.length - 1] : undefined;
    if (last?.unaskedAck) return null;

    const tap = (label: string) => {
        const fieldId = nextRequiredField.id;
        setPendingFieldId(fieldId);
        // `sendTurn` resolves after the turn settles either way. Clearing then
        // is safe on success too: the committed field is no longer
        // next-required, so no row renders for it regardless of the marker.
        sendTurn(label).finally(() =>
            setPendingFieldId((current) => (current === fieldId ? null : current))
        );
    };

    return (
        <div className={styles.chips} role="group" aria-label={`Quick answers for ${nextRequiredField.label}`}>
            {options.map((option) => (
                <button
                    type="button"
                    key={option.value}
                    className={styles.chip}
                    onClick={() => tap(option.label)}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );
};

export default QuickReplyChips;
